import { inject, Injectable } from '@angular/core';
import {
  ListModel,
  ListRequestModel,
  RemoveRiskTagRequestModel,
  RiskTagResponseModel,
  RiskTagStatus,
} from '@itb-monorepo/models';
import { map, Observable, tap, throwError } from 'rxjs';
import { removeEmptyFields } from '@itb-monorepo/helper';
import { HttpMainService } from '../../http-main.service';
import { hasSupporterPermission } from '../../utility-services/supporter-permissions';
import { AdminsService } from './admins.service';

@Injectable({ providedIn: 'root' })
export class SupporterRisksService {
  private readonly http = inject(HttpMainService);
  private readonly admins = inject(AdminsService);
  private readonly baseUrl = '/trade/v1/risk/tags';

  hasAccess(action: 'L' | 'R' | 'S'): boolean {
    return hasSupporterPermission(
      this.admins.currentAdmin?.scopes,
      `risks:${action}`
    );
  }

  getList(
    params: Omit<ListRequestModel, 'setPage' | 'setFilter'>
  ): Observable<ListModel<RiskTagResponseModel>> {
    if (!this.hasAccess('L')) return this.denied();
    return this.loadList(params);
  }

  private loadList(
    params: Omit<ListRequestModel, 'setPage' | 'setFilter'>
  ): Observable<ListModel<RiskTagResponseModel>> {
    return this.http
      .get<ListModel<RiskTagResponseModel>>(
        this.baseUrl,
        removeEmptyFields(params)
      )
      .pipe(map((result) => ({ ...result, elements: result.elements ?? [] })));
  }

  get(tagId: string): Observable<RiskTagResponseModel> {
    if (!this.hasAccess('R')) return this.denied();
    return this.http.get(`${this.baseUrl}/${encodeURIComponent(tagId)}`);
  }

  getUserTags(
    userId: string,
    params: Pick<ListRequestModel, 'take' | 'skip'> & {
      status?: RiskTagStatus;
    } = {}
  ): Observable<ListModel<RiskTagResponseModel>> {
    if (!this.hasAccess('L') || !this.hasAccess('R')) return this.denied();
    return this.loadUserTags(userId, params);
  }

  getUserAlerts(
    userId: string,
    params: Pick<ListRequestModel, 'take' | 'skip'> = {}
  ): Observable<ListModel<RiskTagResponseModel>> {
    if (!this.hasAccess('S')) return this.denied();
    return this.loadUserTags(userId, { ...params, status: 'ACTIVE' });
  }

  private loadUserTags(
    userId: string,
    params: Pick<ListRequestModel, 'take' | 'skip'> & {
      status?: RiskTagStatus;
    }
  ): Observable<ListModel<RiskTagResponseModel>> {
    if (!userId || userId.includes('"')) {
      return throwError(() => new Error('RISK_REVIEW.InvalidUser'));
    }
    const { status, ...pagination } = params;
    return this.loadList({
      ...pagination,
      filter: `user_id:eq("${userId}")${
        status ? `,status:eq("${status}")` : ''
      }`,
      sort: 'desc(activated_at)',
    }).pipe(
      map((result) => ({
        ...result,
        elements: result.elements.filter(
          (tag) => tag.user_id === userId && (!status || tag.status === status)
        ),
      }))
    );
  }

  remove(
    tagId: string,
    request: RemoveRiskTagRequestModel
  ): Observable<RiskTagResponseModel> {
    if (!this.hasAccess('S')) return this.denied();
    const admin_note = request.admin_note.trim();
    if (!admin_note || Array.from(admin_note).length > 500) {
      return throwError(() => new Error('RISK_REVIEW.InvalidNote'));
    }
    return this.http
      .patch<RiskTagResponseModel>(
        `${this.baseUrl}/${encodeURIComponent(tagId)}`,
        {
          admin_note,
        }
      )
      .pipe(
        tap((tag) => {
          if (
            tag.status !== 'REMOVED' ||
            typeof BroadcastChannel === 'undefined'
          )
            return;
          // Notification failure must not turn a completed review into an API error.
          let channel: BroadcastChannel | undefined;
          try {
            channel = new BroadcastChannel('financial-updates');
            channel.postMessage({
              type: 'RISK_TAG_CHANGED',
              userId: tag.user_id,
            });
          } catch {
            // Receipt pages also refresh risk when they regain focus.
          } finally {
            channel?.close();
          }
        })
      );
  }

  private denied(): Observable<never> {
    return throwError(() => new Error('RISK_REVIEW.NoAccess'));
  }
}

export function isRiskTagConflict(error: unknown): boolean {
  const response = error as {
    code?: string | number;
    error?: { code?: string | number };
  };
  const code = response?.error?.code ?? response?.code;
  return (
    code === 9 ||
    code === '9' ||
    code === 'FailedPrecondition' ||
    code === 'FAILED_PRECONDITION'
  );
}
